import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { mockAppraisalGuidelines } from '../../mocks/appraisal-guidelines';

interface GuidelineSection {
  title: string;
  content: string[];
}

export default function AppraisalGuidelinePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guideline, setGuideline] = useState<any>(null);

  useEffect(() => {
    const found = mockAppraisalGuidelines.find(g => g.id === id);
    if (found) {
      setGuideline(found);
    }
  }, [id]);

  if (!guideline) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-file-search-line text-4xl text-slate-500"></i>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Guideline Not Found</h1>
          <p className="text-slate-400 mb-8">The appraisal guideline you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/appraisal-guidelines')}
            className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors"
          >
            Back to Appraisal Guidelines
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const getDetailedSections = (): GuidelineSection[] => {
    // Return detailed content based on guideline ID
    switch (guideline.id) {
      case 'app-001':
        return [
          {
            title: '1. Valuation Approaches',
            content: [
              'Sales Comparison Approach: Primary method for single-family properties',
              'Cost Approach: Required for new construction and unique properties',
              'Income Approach: Used for investment properties and rental units',
              'Reconciliation: Weighing approaches to determine final value'
            ]
          },
          {
            title: '2. Comparable Sales Selection',
            content: [
              'Sales must be closed within the last 12 months',
              'Properties should be similar in size, style, and condition',
              'Location within same neighborhood or competing area',
              'Minimum of 3 comparable sales required',
              'Adjustments must be supported and reasonable'
            ]
          },
          {
            title: '3. Market Value Definition',
            content: [
              'Most probable price in a competitive market',
              'Assumes knowledgeable buyer and seller',
              'Reasonable time for exposure to market',
              'Payment in cash or equivalent financing',
              'No undue stimulus affecting either party'
            ]
          },
          {
            title: '4. Adjustment Guidelines',
            content: [
              'Adjustments based on market-derived data',
              'Gross adjustments typically not to exceed 25%',
              'Net adjustments typically not to exceed 15%',
              'Each adjustment must be explained and supported',
              'Paired sales analysis preferred for major adjustments'
            ]
          }
        ];
      case 'app-002':
        return [
          {
            title: '1. Minimum Property Standards',
            content: [
              'Property must be safe, sound, and secure',
              'All systems must be in working condition',
              'No health or safety hazards present',
              'Adequate heating and cooling systems',
              'Proper ventilation throughout property'
            ]
          },
          {
            title: '2. Required Repairs',
            content: [
              'Structural defects must be corrected',
              'Roof must be weather-tight with remaining life',
              'Foundation must be sound and stable',
              'Electrical, plumbing, and HVAC must function properly',
              'Peeling paint in homes built before 1978 must be addressed'
            ]
          },
          {
            title: '3. Property Condition Assessment',
            content: [
              'C1: New construction or recently renovated',
              'C2: Property has been well maintained',
              'C3: Property shows normal wear and tear',
              'C4: Property has deferred maintenance',
              'C5: Property has significant deterioration',
              'C6: Property is not suitable for occupancy'
            ]
          },
          {
            title: '4. Safety Requirements',
            content: [
              'Smoke detectors on each level',
              'Carbon monoxide detectors where required',
              'Handrails on all stairways',
              'GFCI outlets in wet areas',
              'No exposed wiring or electrical hazards'
            ]
          }
        ];
      case 'app-005':
        return [
          {
            title: '1. Comparable Selection Criteria',
            content: [
              'Proximity: Within 1 mile for urban, wider for rural',
              'Sale Date: Closed within past 12 months preferred',
              'Property Type: Same property type and style',
              'Size: Within 20% of subject property GLA',
              'Condition: Similar condition and quality'
            ]
          },
          {
            title: '2. Adjustment Categories',
            content: [
              'Location: Neighborhood desirability differences',
              'Site: Lot size, view, topography variations',
              'Design/Appeal: Architectural style and curb appeal',
              'Quality: Construction quality and materials',
              'Age/Condition: Effective age and maintenance level',
              'Room Count: Bedroom and bathroom differences',
              'GLA: Gross living area adjustments',
              'Basement: Finished vs. unfinished space',
              'Functional Utility: Floor plan and layout',
              'Amenities: Garage, pool, fireplace, etc.'
            ]
          },
          {
            title: '3. Market Data Analysis',
            content: [
              'Paired sales analysis for major adjustments',
              'Regression analysis for multiple variables',
              'Market extraction from closed sales',
              'Cost approach for depreciation estimates',
              'Income approach for rental features'
            ]
          },
          {
            title: '4. Adjustment Limits',
            content: [
              'Individual adjustments should be reasonable',
              'Gross adjustments typically under 25%',
              'Net adjustments typically under 15%',
              'Large adjustments require strong support',
              'Consider using different comparables if adjustments are excessive'
            ]
          }
        ];
      default:
        return [
          {
            title: '1. Overview',
            content: [
              `${guideline.description}`,
              'This guideline provides comprehensive standards and requirements',
              'All requirements must be met for loan approval',
              'Documentation must support all findings and conclusions'
            ]
          },
          {
            title: '2. Key Requirements',
            content: [
              'Property must meet all applicable standards',
              'Appraisal must be completed by qualified appraiser',
              'All forms must be properly completed',
              'Supporting documentation must be included',
              'Quality control review may be required'
            ]
          },
          {
            title: '3. Documentation Standards',
            content: [
              'Clear and detailed property description',
              'Photographs of subject and comparables',
              'Market analysis and trends',
              'Explanation of adjustments',
              'Reconciliation of value approaches'
            ]
          },
          {
            title: '4. Compliance Requirements',
            content: [
              'Follow all agency-specific guidelines',
              'Maintain appraiser independence',
              'Use current forms and standards',
              'Meet delivery and timing requirements',
              'Ensure accuracy and completeness'
            ]
          }
        ];
    }
  };

  const sections = getDetailedSections();

  const handleDownload = () => {
    alert('PDF download functionality would be implemented here');
  };

  const handleBookmark = () => {
    alert('Bookmark functionality would be implemented here');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: guideline.title,
        text: guideline.description,
        url: window.location.href,
      });
    } else {
      alert('Share functionality not supported on this device');
    }
  };

  const relatedGuidelines = mockAppraisalGuidelines
    .filter(g => g.id !== guideline.id && (g.category === guideline.category || g.source === guideline.source))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-emerald-400 cursor-pointer transition-colors"
            >
              Home
            </button>
            <i className="ri-arrow-right-s-line text-slate-600"></i>
            <button
              onClick={() => navigate('/appraisal-guidelines')}
              className="text-slate-400 hover:text-emerald-400 cursor-pointer transition-colors"
            >
              Appraisal Guidelines
            </button>
            <i className="ri-arrow-right-s-line text-slate-600"></i>
            <span className="text-slate-300">{guideline.title}</span>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <span className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  {guideline.source}
                </span>
                <span className="bg-slate-700 text-slate-300 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                  {guideline.category}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {guideline.title}
              </h1>
              <p className="text-lg text-slate-300 mb-4">
                {guideline.description}
              </p>
              <div className="flex items-center text-sm text-slate-400">
                <i className="ri-calendar-line mr-2"></i>
                Last Updated: {new Date(guideline.lastUpdated).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownload}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
            >
              <i className="ri-download-line mr-2"></i>
              Download PDF
            </button>
            <button
              onClick={handleBookmark}
              className="bg-slate-700 text-slate-300 px-6 py-3 rounded-lg hover:bg-slate-600 font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
            >
              <i className="ri-bookmark-line mr-2"></i>
              Bookmark
            </button>
            <button
              onClick={handleShare}
              className="bg-slate-700 text-slate-300 px-6 py-3 rounded-lg hover:bg-slate-600 font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
            >
              <i className="ri-share-line mr-2"></i>
              Share
            </button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-8">
            {sections.map((section, index) => (
              <div key={index} className={index > 0 ? 'mt-8' : ''}>
                <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-emerald-500 mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">Related Topics</h3>
            <div className="flex flex-wrap gap-2">
              {guideline.tags.map((tag: string, index: number) => (
                <span
                  key={index}
                  className="bg-slate-800 text-slate-300 text-sm px-4 py-2 rounded-full border border-slate-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Related Guidelines */}
          {relatedGuidelines.length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-white mb-6">Related Guidelines</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedGuidelines.map((related) => (
                  <div
                    key={related.id}
                    className="bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/appraisal-guideline/${related.id}`)}
                  >
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="bg-emerald-600 text-white text-xs font-semibold px-2 py-1 rounded whitespace-nowrap">
                        {related.source}
                      </span>
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                      {related.description}
                    </p>
                    <div className="flex items-center text-emerald-400 text-sm font-medium">
                      View Guideline
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
